import {
  absBig,
  BIG_ZERO,
  gcdBig,
  parseBigIntToken,
} from "@/lib/tool-kit/math/bigint";
import {
  factorize,
  MAX_FACTOR_INPUT,
  type PrimePower,
} from "@/lib/tool-kit/math/primes";

/**
 * Greatest common factor and least common multiple, two ways.
 *
 * Euclid's algorithm is the method that scales: each division step at least halves the
 * remainder every two steps, so even forty-digit numbers finish in a couple of hundred
 * divisions. The prime-factorisation method is the one taught first — common primes at
 * their lowest power for the GCF, every prime at its highest for the LCM — and is shown
 * beside it whenever every number is small enough to factor.
 */

export interface EuclidStep {
  dividend: bigint;
  divisor: bigint;
  quotient: bigint;
  remainder: bigint;
}

export interface GcfStage {
  left: bigint;
  right: bigint;
  gcd: bigint;
  steps: EuclidStep[];
}

export interface LcmStage {
  left: bigint;
  right: bigint;
  gcd: bigint;
  lcm: bigint;
}

export interface FactorRow {
  prime: bigint;
  /** This prime's exponent in each number, in input order (0 when absent). */
  exponents: number[];
  min: number;
  max: number;
}

export interface GcfLcmResult {
  /** Absolute values of the numbers, in input order. */
  values: bigint[];
  hadNegative: boolean;
  gcf: bigint;
  lcm: bigint;
  gcfStages: GcfStage[];
  lcmStages: LcmStage[];
  /** Null when a number exceeds the factoriser's ceiling. */
  factorRows: FactorRow[] | null;
  factorizations: PrimePower[][] | null;
}

export const MAX_LIST_LENGTH = 12;
export const MAX_INTEGER_DIGITS = 40;

const LIST_SEPARATORS = /[\s,;]+/;

export interface IntegerList {
  values: bigint[];
  /** Tokens that are not whole numbers, as typed. */
  invalid: string[];
  hasZero: boolean;
  tooLong: boolean;
}

/** Whole numbers separated by commas, semicolons, spaces or new lines. */
export function parseIntegerList(raw: string): IntegerList {
  const values: bigint[] = [];
  const invalid: string[] = [];
  let tooLong = false;
  for (const token of raw.split(LIST_SEPARATORS)) {
    if (token === "") {
      continue;
    }
    const value = parseBigIntToken(token);
    if (value === null) {
      invalid.push(token);
      continue;
    }
    if (absBig(value).toString().length > MAX_INTEGER_DIGITS) {
      tooLong = true;
    }
    values.push(value);
  }
  return {
    values,
    invalid,
    hasZero: values.some((v) => v === BIG_ZERO),
    tooLong: tooLong || values.length > MAX_LIST_LENGTH,
  };
}

/** Euclid's algorithm with every division written down: a = q·b + r. */
export function euclidSteps(
  a: bigint,
  b: bigint
): { gcd: bigint; steps: EuclidStep[] } {
  let dividend = absBig(a);
  let divisor = absBig(b);
  if (dividend < divisor) {
    [dividend, divisor] = [divisor, dividend];
  }
  const steps: EuclidStep[] = [];
  while (divisor !== BIG_ZERO) {
    const quotient = dividend / divisor;
    const remainder = dividend % divisor;
    steps.push({ dividend, divisor, quotient, remainder });
    dividend = divisor;
    divisor = remainder;
  }
  return { gcd: dividend, steps };
}

function factorTable(values: readonly bigint[]) {
  if (values.some((v) => v > MAX_FACTOR_INPUT)) {
    return { rows: null, factorizations: null };
  }
  const factorizations = values.map((v) => factorize(v).factors);
  const primes = [
    ...new Set(factorizations.flatMap((f) => f.map((p) => p.prime))),
  ].sort((a, b) => (a < b ? -1 : 1));
  const rows = primes.map((prime) => {
    const exponents = factorizations.map(
      (f) => f.find((p) => p.prime === prime)?.exponent ?? 0
    );
    return {
      prime,
      exponents,
      min: Math.min(...exponents),
      max: Math.max(...exponents),
    };
  });
  return { rows, factorizations };
}

/**
 * GCF and LCM of two or more nonzero integers (signs are dropped: both are defined on
 * absolute values). For more than two numbers the pairwise results are chained:
 * GCF(a, b, c) = GCF(GCF(a, b), c), and likewise for the LCM.
 */
export function computeGcfLcm(input: readonly bigint[]): GcfLcmResult {
  const values = input.map(absBig);
  const gcfStages: GcfStage[] = [];
  const lcmStages: LcmStage[] = [];
  let gcf = values[0];
  let lcm = values[0];
  for (const value of values.slice(1)) {
    const stage = euclidSteps(gcf, value);
    gcfStages.push({ left: gcf, right: value, ...stage });
    gcf = stage.gcd;

    const pairGcd = gcdBig(lcm, value);
    const next = (lcm / pairGcd) * value;
    lcmStages.push({ left: lcm, right: value, gcd: pairGcd, lcm: next });
    lcm = next;
  }
  const { rows, factorizations } = factorTable(values);
  return {
    values,
    hadNegative: input.some((v) => v < BIG_ZERO),
    gcf,
    lcm,
    gcfStages,
    lcmStages,
    factorRows: rows,
    factorizations,
  };
}

/** Π prime^exponent over the rows, choosing each row's min (GCF) or max (LCM). */
export function productFromRows(
  rows: readonly FactorRow[],
  pick: "min" | "max"
): PrimePower[] {
  return rows
    .map((row) => ({ prime: row.prime, exponent: row[pick] }))
    .filter((p) => p.exponent > 0);
}
